import {
  AddonSubscriptionOutSchema,
  AddonsViewsNinjaAddonGetAllAddonPlansTermEnum,
} from "@/api/client";
import { handleError } from "@/api/handleError";
import {
  getAllAddonPlans,
  getAllSubscribedAddons,
} from "@/api/requests/addons";
import { getBusinessSubscription } from "@/api/requests/subscription";
import { processAddons } from "@/helpers/add-ons/index";
import { PINIA_PERSIST_OPTIONS } from "@/helpers/constants";
import { defineStore } from "pinia";
import { useBusinessStore } from "..";
import { Addons, UserGuides } from "../types";

interface AddonState {
  allAddons: Addons[];
  subscribedAddons: AddonSubscriptionOutSchema[];
  selectedAddon?: Addons | null;
  userGuides: UserGuides;
  subscriptionTerm?: AddonsViewsNinjaAddonGetAllAddonPlansTermEnum | null;
  salesCampaign: {
    pendingCampaignId?: number | null;
    oldNumberId?: number | null;
    selectedNumber?: string | null;
  };
}

export const useAddonStore = defineStore("add-ons", {
  state: (): AddonState => ({
    allAddons: [],
    subscriptionTerm: null,
    subscribedAddons: [],
    selectedAddon: null,
    userGuides: {
      liveWidget: {
        videoLink:
          "https://www.youtube.com/embed/ZXJaYBvVLkc?si=2J6_WImNbwMO5wEp",
        text: "How to set up Website Live Call",
        desc: `Setting up your live call widget is quick and easy!
            Follow our simple step-by-step guide to integrate the widget with
            your website and connect your Pressone account.`,
        height: "180",
        width: "250",
        userGuidePdf:
          "https://pressone.slite.page/p/clNnKs_bTrjiKD/PressOne-website-Live-Call-User-Guide",
      },
      voiceOtp: {
        videoLink:
          "https://www.youtube.com/embed/nCAk1TLCXds?si=TI3PYO8gnIF24WHe",
        text: "How to set up Voice OTP Add-On",
        desc: " We will walk you through the step-by-step process of how setup your addon.",
        height: "130",
        width: "280",
      },
      textToSpeech: {
        videoLink:
          "https://www.youtube.com/embed/Ijxx7KnCuhQ?si=wuOCAkrcBVa8S6RO",
        text: "How to set up Text to Speech Add-On",
        desc: " We will walk you through the step-by-step process of how setup your addon.",
        height: "130",
        width: "280",
      },
    },
    salesCampaign: {
      pendingCampaignId: null,
      oldNumberId: null,
      selectedNumber: null,
    },
  }),

  getters: {
    getAllAddons: (state) => state.allAddons,
    getSubscribedAddons: (state) => state.subscribedAddons,
    getSubscriptionTerm: (state) => state.subscriptionTerm,
    getSelectedAddon: (state) => state.selectedAddon,
    getAllEnhancedAddons: (state) => {
      const enhancedAddons = state.allAddons.map((addon) => {
        const purchased = state.subscribedAddons.find(
          (purchased) => purchased.subscription_plan.id === addon.id
        );
        const isPurchased = !!purchased;
        const subscribedId = purchased ? purchased.id : 0;

        const res = {
          ...addon,
          isPurchased,
          subscribedId,
        };

        return res;
      });
      return processAddons(enhancedAddons);
    },
  },

  actions: {
    async fetchSubscriptionTerm() {
      const businessStore = useBusinessStore();

      const businessId = businessStore.currentBusinessId;

      try {
        if (!businessId) {
          throw new Error("Business id not found");
        }

        const response = await getBusinessSubscription({ businessId });

        this.subscriptionTerm = response.data.data
          .term as AddonsViewsNinjaAddonGetAllAddonPlansTermEnum;

        businessStore.subscriptionDetails = response?.data.data;
      } catch (error) {
        handleError({
          error,
          fallbackMessage: "Failed to fetch subscription term",
        });
      }
    },
    async fetchAllAddons() {
      const businessStore = useBusinessStore();
      const businessId = businessStore.currentBusinessId;

      try {
        const term = this.subscriptionTerm || "monthly";

        if (!businessId) {
          throw new Error("Business id not found");
        }

        const res = await getAllAddonPlans({ term, businessId });

        this.allAddons = res?.data.data || [];
      } catch (error) {
        handleError({
          error,
          fallbackMessage: "Failed to fetch all addons",
        });
      }
    },
    async fetchSubscribedAddons() {
      const businessStore = useBusinessStore();
      const businessId = businessStore.currentBusinessId;

      try {
        if (!businessId) {
          throw new Error("Business id not found");
        }

        const [subscribedAddonsResponse] = await Promise.all([
          getAllSubscribedAddons({ businessId }),
          this.fetchSubscriptionTerm(),
        ]);

        this.subscribedAddons = subscribedAddonsResponse?.data.data || [];
      } catch (error) {
        handleError({
          error,
          fallbackMessage: "Failed to fetch subscribed addons",
        });
      }
    },
    async fetchAllData() {
      await this.fetchSubscriptionTerm();
      await Promise.all([this.fetchAllAddons(), this.fetchSubscribedAddons()]);
    },
  },
  persist: PINIA_PERSIST_OPTIONS,
});
